import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import FileConstants from '../../../FileConstants'
import ENVIRONMENT from '../../../environments';

const CUSTOMER_USERNAME = FileConstants.CUSTOMER_USERNAME;

const SCENE_KEY = 'ALL3D_SCENE';
const decodeSceneId = (encoded) => {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
  const xored = atob(padded);
  let result = '';
  for (let i = 0; i < xored.length; i++) {
    result += String.fromCharCode(xored.charCodeAt(i) ^ SCENE_KEY.charCodeAt(i % SCENE_KEY.length));
  }
  return result;
};

const AiSceneCreatorIframe = ({ location }) => {
  const iframeRef = useRef(null);
  const iframeSrc = useMemo(() => {
    const baseUrl = ENVIRONMENT.AI_SCENE_CREATOR_URL || 'https://darpit0wkkhwg.cloudfront.net/';
    const params = new URLSearchParams(location?.search || '');
    const rawSceneId = params.get('ai_scene_id');

    if (rawSceneId) {
      if (/^\d+$/.test(rawSceneId)) {
        // already a plain integer, no decode needed
      } else {
        try {
          const decoded = decodeSceneId(rawSceneId);
          const asInt = parseInt(decoded, 10);
          if (!isNaN(asInt)) {
            params.set('ai_scene_id', asInt);
          } else {
            console.warn('[AiSceneCreatorIframe] decoded value is not a valid integer:', decoded);
          }
        } catch (e) {
          console.warn('[AiSceneCreatorIframe] failed to decode ai_scene_id:', e);
        }
      }
    }

    const search = params.toString();
    const separator = baseUrl.includes('?') ? '&' : '?';
    return search ? `${baseUrl}${separator}${search}` : baseUrl;
  }, [location?.search]);

  const sendUsernameToIframe = useCallback((targetOrigin) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    try {
      const origin = targetOrigin != null ? targetOrigin : new URL(iframeSrc).origin;
      iframe.contentWindow.postMessage(
        {
          username: CUSTOMER_USERNAME,
          customerUsername: CUSTOMER_USERNAME,
          company_id: localStorage.getItem('company_id')
        },
        origin
      );
    } catch (_) {
      // Invalid URL or postMessage failed
    }
  }, [iframeSrc]);

  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('ai_scene_render_channel');
    } catch (e) {
      // BroadcastChannel not supported
    }

    const handleMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'SCENE_CREATOR_READY') {
        sendUsernameToIframe(event.origin);
        return;
      }

      if (data.type === 'request_token_refresh') {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) return;
        try {
          iframe.contentWindow.postMessage(
            { token: localStorage.getItem('all3d_jwt_token') },
            event.origin
          );
        } catch (_) {}
        return;
      }

      if (data.type === 'AI_RENDER_REQUESTED' && bc) {
        const payload = data.payload != null ? data.payload : {
          camera_name: data.camera_name,
          sceneId: data.sceneId,
          taskId: data.taskId,
          platform_request_id: data.platform_request_id,
        };
        console.log('AI_SCENE_RENDER_REQUESTED', payload);
        bc.postMessage({ type: 'AI_SCENE_RENDER_REQUESTED', payload });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (bc) bc.close();
    };
  }, [sendUsernameToIframe]);

  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
      <iframe
        ref={iframeRef}
        title="AI Scene Creator"
        src={iframeSrc}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        allowFullScreen
      />
    </div>
  );
};

export default AiSceneCreatorIframe;